import { PayloadAction, createSlice } from '@reduxjs/toolkit'

import { DEFAULT_SHOWCASE_ROOM } from '@store/constants'
import { RootStateTypeExtra } from '@store/types'

const currentOfflineMode = localStorage.getItem('offlineMode') === 'true'
const currentFirebasePort = localStorage.getItem('firebasePort')
const currentFirebaseIp = localStorage.getItem('firebaseIp')
const currentEnvisionAssetConfig = localStorage.getItem('envisionAssetConfig')
const currentRoom = localStorage.getItem('room')
const currentShortlistPropertyHash = localStorage.getItem(
  'shortlistPropertyHash'
)

const defaultAgentFlag = localStorage.getItem('defaultAgent') === 'true'

export interface EnvisionAssetConfigInterface {
  host: string
  port: string
}

export interface AppConfigInterface {
  fullscreen: boolean
  offlineMode: boolean
  firebasePort: string
  firebaseIp: string
  envisionAssetConfig: EnvisionAssetConfigInterface
  room: string
  shortlistPropertyHash: string
  defaultAgent: boolean
  slideshowIsPlaying: boolean
  videoIsPlaying: boolean
}

const initialState = {
  fullscreen: false,
  offlineMode: currentOfflineMode || false,
  firebasePort: currentFirebasePort || '',
  firebaseIp: currentFirebaseIp || 'localhost',
  envisionAssetConfig: currentEnvisionAssetConfig
    ? JSON.parse(currentEnvisionAssetConfig)
    : {
        host: '127.0.0.1',
        port: '',
      },
  room: currentRoom || '',
  shortlistPropertyHash: currentShortlistPropertyHash || '',
  defaultAgent: defaultAgentFlag || false,
  slideshowIsPlaying: false,
  videoIsPlaying: false,
} as AppConfigInterface

const appConfigSlice = createSlice({
  name: 'appConfig',
  initialState,
  reducers: {
    setFullScreen: (state, action: PayloadAction<boolean>) => ({
      ...state,
      fullscreen: action.payload,
    }),
    setOfflineMode: (state, action: PayloadAction<boolean>) => {
      localStorage.setItem('offlineMode', JSON.stringify(action.payload))
      return {
        ...state,
        offlineMode: action.payload,
      }
    },
    setFirebasePort: (state, action: PayloadAction<string>) => {
      localStorage.setItem('firebasePort', action.payload)
      return {
        ...state,
        firebasePort: action.payload,
      }
    },
    setFirebaseIp: (state, action: PayloadAction<string>) => {
      localStorage.setItem('firebaseIp', action.payload)
      return {
        ...state,
        firebaseIp: action.payload,
      }
    },
    setEnvisionAssetConfig: (
      state: AppConfigInterface,
      action: PayloadAction<EnvisionAssetConfigInterface>
    ) => {
      localStorage.setItem(
        'envisionAssetConfig',
        JSON.stringify(action.payload)
      )
      return { ...state, envisionAssetConfig: action.payload }
    },
    setRoom: (state, action: PayloadAction<string>) => {
      let room = action.payload
      if (action.payload.toLowerCase() === DEFAULT_SHOWCASE_ROOM) {
        room = ''
      }
      localStorage.setItem('room', room)
      return {
        ...state,
        ...{ room },
      }
    },
    setShortlistPropertyHash: (state, action: PayloadAction<string>) => {
      localStorage.setItem('shortlistPropertyHash', action.payload)
      return {
        ...state,
        ...{ shortlistPropertyHash: action.payload },
      }
    },
    setDefaultAgent: (state, action: PayloadAction<boolean>) => {
      localStorage.setItem('defaultAgent', JSON.stringify(action.payload))
      return {
        ...state,
        ...{ defaultAgent: action.payload },
      }
    },
    setSlideshowIsPlaying: (state, action: PayloadAction<boolean>) => ({
      ...state,
      ...{ slideshowIsPlaying: action.payload },
    }),
    setVideoIsPlaying: (state, action: PayloadAction<boolean>) => ({
      ...state,
      ...{ videoIsPlaying: action.payload },
    }),
    removeAppConfig: (state) => {
      localStorage.removeItem('offlineMode')
      localStorage.removeItem('firebasePort')
      localStorage.removeItem('firebaseIp')
      localStorage.removeItem('envisionAssetConfig')
      localStorage.removeItem('room')
      localStorage.removeItem('shortlistPropertyHash')
      localStorage.removeItem('defaultAgent')
      return {
        ...state,
        offlineMode: false,
        firebaseIp: 'localhost',
        firebasePort: '',
        envisionAssetConfig: {
          host: '127.0.0.1',
          port: '',
        },
        room: '',
        shortlistPropertyHash: '',
        defaultAgent: false,
        slideshowIsPlaying: false,
        videoIsPlaying: false,
      }
    },
  },
})

export const {
  setFullScreen,
  setOfflineMode,
  setFirebasePort,
  setEnvisionAssetConfig,
  setRoom,
  setFirebaseIp,
  removeAppConfig,
  setShortlistPropertyHash,
  setDefaultAgent,
  setSlideshowIsPlaying,
  setVideoIsPlaying,
} = appConfigSlice.actions

export const selectFullscreen = (state: RootStateTypeExtra) =>
  state.appConfig.fullscreen
export const selectOfflineMode = (state: RootStateTypeExtra) => state.appConfig

export const selectCurrentRoom = (state: RootStateTypeExtra) => {
  const room = state.appConfig?.room || DEFAULT_SHOWCASE_ROOM
  const isDefault = room.toLowerCase() === DEFAULT_SHOWCASE_ROOM

  if (isDefault) {
    return DEFAULT_SHOWCASE_ROOM
  }

  return room
}

export default appConfigSlice.reducer
